import { Container } from "@fluidattacks/design";
import isEmpty from "lodash/isEmpty";
import { useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Banner } from "./banner";
import { FindingsTable } from "./findings-table";
import { useGetFindingFiltersStore } from "./findings-table/hooks/use-finding-filters-store";
import { useFindingsQuery } from "./hooks";

import { groupContext } from "../context";
import { EmptyVulnerabilities } from "features/empty-vulnerabilities";
import { ManagedType } from "gql/graphql";
import type { FindingFiltersInput, GetFindingsQueryQuery } from "gql/graphql";

const GroupFindings: React.FC = (): JSX.Element => {
  const { groupName } = useParams() as { groupName: string };
  const { setIsTestingYourApp } = useContext(groupContext);

  const filtersStore = useGetFindingFiltersStore(groupName);

  const { findings, group, loading, refetch } = useFindingsQuery(
    groupName,
    filtersStore as unknown as FindingFiltersInput,
  );

  const isGroupInTrial = group?.managed === ManagedType.Trial;

  const showTestingYourAppLoader = useMemo((): boolean => {
    const noSearching = !loading && filtersStore.search !== undefined;
    const noFindings = isEmpty(findings);
    const isNotHacker = group?.userRole !== "hacker";

    const areFiltersEmpty = Object.values(filtersStore).every(isEmpty);

    return noFindings && noSearching && areFiltersEmpty && isNotHacker;
  }, [filtersStore, findings, group?.userRole, loading]);

  useEffect((): void => {
    if (setIsTestingYourApp && !loading) {
      setIsTestingYourApp(showTestingYourAppLoader);
    }
  }, [loading, setIsTestingYourApp, showTestingYourAppLoader]);

  return (
    <Container id={"users"} minHeight={"100%"} px={1.25} py={1.25}>
      {isGroupInTrial && <Banner />}

      {showTestingYourAppLoader ? (
        <EmptyVulnerabilities />
      ) : (
        <FindingsTable
          findings={findings}
          group={group as GetFindingsQueryQuery["group"]}
          loading={loading}
          refetch={refetch}
        />
      )}
    </Container>
  );
};

export { GroupFindings };
